/**
 * Small plan usage widget for Creator Dashboard.
 * Endpoint: GET /api/plans/me/usage
 * Never blocks the page if data is missing.
 */
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { CreditCard, AlertCircle } from 'lucide-react';
import theme from '../theme';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PlanUsageCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const auth = useMemo(() => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  }), []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/plans/me/usage`, auth);
        if (!cancelled) setData(res.data || null);
      } catch (e) {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [auth]);

  if (loading) return null;

  const card = {
    padding: 14, background: theme.colors.bg.card,
    border: `1px solid ${theme.colors.borders}`,
    borderRadius: theme.radius.card, marginBottom: 12,
  };

  if (!data || !data.synced) {
    return (
      <div data-testid="plan-usage-card-unsynced" style={card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: theme.colors.text.muted, fontWeight: 700, fontSize: 13 }}>
          <AlertCircle size={14} /> Plan: Not synced yet
        </div>
      </div>
    );
  }

  const used = Number(data.active_kickback_used || 0);
  const limit = data.active_kickback_limit;
  const pct = (limit && typeof limit === 'number') ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const statusColor =
    data.membership_status === 'active' ? theme.colors.status.success :
    data.membership_status === 'free' ? theme.colors.status.info :
    data.membership_status === 'canceled' ? theme.colors.text.muted :
    theme.colors.status.warning;

  return (
    <div data-testid="plan-usage-card" style={card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <CreditCard size={14} color={theme.colors.primary} />
          <span style={{ fontWeight: 800, color: theme.colors.text.primary }}>{data.plan_name}</span>
          <span style={{
            padding: '2px 8px', borderRadius: 999,
            background: `${statusColor}1A`, color: statusColor,
            fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5,
          }}>{data.membership_status}</span>
        </div>
        <div data-testid="plan-usage-count" style={{ fontSize: 13, color: theme.colors.text.secondary, fontWeight: 600 }}>
          {used} of {limit ?? 'unlimited'} Active Kickbacks used on your plan
        </div>
      </div>
      {typeof limit === 'number' && (
        <div style={{ marginTop: 10, height: 6, background: theme.colors.bg.tertiary, borderRadius: 999, overflow: 'hidden' }}>
          <div style={{
            height: '100%', width: `${pct}%`,
            background: pct >= 100 ? theme.colors.status.error :
                        pct >= 80 ? theme.colors.status.warning :
                        theme.colors.primary,
          }} />
        </div>
      )}
    </div>
  );
}
